import { type Track } from "../../../graphql.js";
import { ENTITY_TYPE } from "../../../settings/settings.js";
import { type GetGenomeStatistics } from "./useViewerGraphql.js";

export enum PIN_TRACK {
  TOP = "top",
  BOTTOM = "bottom",
}

export const SCALE_FACTOR_MOUSE_WHEEL_ZOOM = 1.2;
export const MIN_VIEW_RANGE_SIZE = 41;

export const TRACK_TYPE = {
  GENE: ENTITY_TYPE.GENE,
  TRANSCRIPT_LOCATION: ENTITY_TYPE.TRANSCRIPT_LOCATION,
  PROTEIN_LOCATION: ENTITY_TYPE.PROTEIN_LOCATION,
  VARIANT: ENTITY_TYPE.VARIANT,
  VCF_VARIANT: ENTITY_TYPE.VCF_VARIANT,
  SEQUENCE: "sequence", // not really the same as ENTITY_TYPE.SEQUENCE
  REGION_FLAG: ENTITY_TYPE.REGION_FLAG,
  BLAST_ALIGNMENT: "blastAlignment",
  PROFILE: "profile",
  NGS: "ngs",
  BED: ENTITY_TYPE.BED_FEATURE,
  NUFEATURE: ENTITY_TYPE.NUFEATURE,
  AAFEATURE: ENTITY_TYPE.AAFEATURE, // e.g. PFAM
  MARKER_NUS: ENTITY_TYPE.MARKER_NUS,
};

export interface ViewerTrack extends Track {
  hidden: boolean;
  color?: string | null;
  noData?: boolean;
}

// FIXME: Not sure this is need, each could hold its own data instead.
export const NAMED_TRACKS: ViewerTrack[] = [
  {
    name: "Flags",
    type: TRACK_TYPE.REGION_FLAG,
    hidden: false,
    pinned: PIN_TRACK.TOP,
    subTypeId: null,
    trackCustomization: null,
  },
  {
    name: "Sequence",
    type: TRACK_TYPE.SEQUENCE,
    hidden: false,
    pinned: PIN_TRACK.TOP,
    subTypeId: null,
    trackCustomization: null,
  },
  { name: "Gene", type: TRACK_TYPE.GENE, hidden: true, pinned: null, subTypeId: null, trackCustomization: null },
  {
    name: "Protein",
    type: TRACK_TYPE.PROTEIN_LOCATION,
    hidden: true,
    pinned: null,
    subTypeId: null,
    trackCustomization: null,
  },
  {
    name: "Transcript",
    type: TRACK_TYPE.TRANSCRIPT_LOCATION,
    hidden: true,
    pinned: null,
    subTypeId: null,
    trackCustomization: null,
  },
  {
    name: "Derived Variants",
    type: TRACK_TYPE.VARIANT,
    hidden: true,
    pinned: null,
    subTypeId: null,
    trackCustomization: null,
  },
  {
    name: "Variants",
    type: TRACK_TYPE.VCF_VARIANT,
    hidden: true,
    pinned: null,
    subTypeId: null,
    trackCustomization: null,
  },
  {
    name: "BLAST HSPs",
    type: TRACK_TYPE.BLAST_ALIGNMENT,
    hidden: true,
    pinned: null,
    subTypeId: null,
    trackCustomization: null,
  },
  {
    name: "NGS",
    type: TRACK_TYPE.NGS,
    hidden: true,
    pinned: null,
    subTypeId: null,
    trackCustomization: null,
  },
  {
    name: "BED Features",
    type: TRACK_TYPE.BED,
    hidden: true,
    pinned: null,
    subTypeId: null,
    trackCustomization: null,
  },
];

export function getViewerTracks(tracks: string[]): ViewerTrack[] {
  return tracks.map(
    (type) =>
      NAMED_TRACKS.find((track) => track.type === type) ?? {
        name: "",
        type,
        hidden: true,
        pinned: null,
        subTypeId: null,
        trackCustomization: null,
      },
  );
}

export function getAllTracks(
  preConfiguredTracks: ViewerTrack[],
  genome: GetGenomeStatistics,
  additionalVisibleTracks: ViewerTrack[] = [],
  userDefinedTracks: ViewerTrack[] = [],
): ViewerTrack[] {
  const allTracks: ViewerTrack[] = [];

  // --- Utils ---
  type ProfileTypes = Array<{ id: any; name: string | null; color?: string | null } | null> | null;

  const isSameTrack = (track1: Omit<ViewerTrack, "hidden">, track2: Omit<ViewerTrack, "hidden">) =>
    track1.type === track2.type && (!track1.subTypeId || track1.subTypeId === track2.subTypeId);

  const isTrackHidden = (track: Omit<ViewerTrack, "hidden">) =>
    !(
      additionalVisibleTracks.concat(userDefinedTracks)?.find((_track) => isSameTrack(_track, track)) &&
      !noDataAvailable(track, genome)
    );

  const addDynamicTrack = (types: ProfileTypes | null, trackType: string) => {
    types?.forEach((type) => {
      if (!allTracks.find((track) => track.type === trackType && track.name === type?.name)) {
        const track: Omit<ViewerTrack, "hidden"> = {
          name: type?.name ?? "Unknown",
          type: trackType,
          subTypeId: type?.id,
          trackCustomization: null,
          pinned: null,
        };
        allTracks.push({
          ...track,
          hidden: isTrackHidden(track),
        });
      }
    });
  };

  const checkTypes = (types: ProfileTypes, subTypeId: any) => {
    const type = types?.find((type) => type?.id === subTypeId);
    return type ? { name: type.name ?? null, color: type.color } : null;
  };
  // ---

  // 1. Verify if the current pre-configured tracks list has tracks that doesn't make sense for the current genome
  // As these tracks are already set to visible (hidden=false) we don't check again the ?add_tracks url param
  preConfiguredTracks.forEach((track) => {
    let typeData: { name: string | null; color?: string | null } = {
      name: null,
      color: undefined,
    };

    switch (track.type) {
      // make sure the NUFEATURE track exists for this genome
      case TRACK_TYPE.NUFEATURE: {
        const nuFeatureType = checkTypes(genome.nuFeatureTypes, track.subTypeId);
        if (nuFeatureType) {
          // we need to annotate the track with the color in case the track was restored from a saved view
          typeData = nuFeatureType;
        } else {
          return;
        }
        break;
      }
      case TRACK_TYPE.AAFEATURE: {
        const aaFeatureType = checkTypes(genome.aaFeatureTypes, track.subTypeId);
        if (aaFeatureType) {
          // we need to annotate the track with the color in case the track was restored from a saved view
          typeData = aaFeatureType;
        } else {
          return;
        }
        break;
      }
      // make sure the MARKER track exists for this genome
      case TRACK_TYPE.MARKER_NUS: {
        const markerType = checkTypes(genome.markerTypes, track.subTypeId);
        if (markerType) {
          typeData = markerType;
        } else {
          return;
        }
        break;
      }
      // make sure the PROFILE track exists for this genome
      case TRACK_TYPE.PROFILE: {
        const nuProfileType = checkTypes(genome.nuProfileTypes, track.subTypeId);
        if (nuProfileType) {
          typeData = nuProfileType;
        } else {
          return;
        }
        break;
      }
      default: {
        const defaultTrack = NAMED_TRACKS.find((defaultTrack) => defaultTrack.type === track.type);
        typeData.name = defaultTrack?.name ?? "Unknown";
        break;
      }
    }

    // TODO: Also check trackCustomization from genome-browser. Should maybe be done in the track itself?
    // line ~1150

    allTracks.push({ ...track, ...typeData, hidden: false });
  });

  // 2. Add standard tracks if not already added
  NAMED_TRACKS.forEach((track) => {
    if (!allTracks.find((visibleTrack) => visibleTrack.type === track.type)) {
      // if not already added to the tracks list we add it
      allTracks.push({ ...track, hidden: isTrackHidden(track) });
    }
  });

  // 3. Add dynamic tracks if not already added
  addDynamicTrack(genome.nuProfileTypes, TRACK_TYPE.PROFILE);
  addDynamicTrack(genome.nuFeatureTypes, TRACK_TYPE.NUFEATURE);
  addDynamicTrack(genome.aaFeatureTypes, TRACK_TYPE.AAFEATURE);
  addDynamicTrack(genome.markerTypes, TRACK_TYPE.MARKER_NUS);

  /** Get pinning information from user defined tracks */
  allTracks.forEach((visibleTrack) => {
    const userTrack = userDefinedTracks.find((userTrack) => isSameTrack(visibleTrack, userTrack));
    if (userTrack) {
      visibleTrack.pinned = userTrack?.pinned;
    }
  });

  return allTracks.map((track) => ({ ...track, noData: noDataAvailable(track, genome) }));
}

const noDataAvailable = (track: Omit<ViewerTrack, "hidden">, genome: GetGenomeStatistics): boolean => {
  switch (track.type) {
    case TRACK_TYPE.VARIANT:
      return !genome.hasGenomeAnalysis;
    case TRACK_TYPE.VCF_VARIANT:
      return !genome.hasVcfGenomeAnalysis;
    case TRACK_TYPE.AAFEATURE:
      // eslint-disable-next-line no-case-declarations
      const aaFeatureStats = genome.genomeStatistics?.aaFeaturesCount?.find(
        (stats) => track.subTypeId === stats?.featureTypeId,
      );
      return !aaFeatureStats?.count;
    case TRACK_TYPE.NUFEATURE:
      // eslint-disable-next-line no-case-declarations
      const nuFeatureStats = genome.genomeStatistics?.nuFeaturesCount?.find(
        (stats) => track.subTypeId === stats?.featureTypeId,
      );
      return !nuFeatureStats?.count;
    case TRACK_TYPE.MARKER_NUS:
      // eslint-disable-next-line no-case-declarations
      const markerStats = genome.genomeStatistics?.markersCount?.find(
        (stats) => track.subTypeId === stats?.markerTypeId,
      );
      return !markerStats?.count;
    case TRACK_TYPE.PROFILE:
      // eslint-disable-next-line no-case-declarations
      const nuProfileStats = genome.genomeStatistics?.nuProfilesCount?.find(
        (stats) => track.subTypeId === stats?.profileTypeId,
      );
      return !nuProfileStats?.count;
    default:
      return false;
  }
};
